// ═══════════════════════════════════════════════════════════════════
// SNAPSHOT DOMAIN LOGIC
//
// A snapshot is a frozen copy of the complete settings object plus a
// label and a date. Once written it is never modified — that is what
// makes it a record rather than a projection.
//
// Three things are built on top of them:
//   1. Trends   — actual recorded balances over time (real history).
//   2. Replay   — re-run a snapshot's own forecast exactly as it looked.
//   3. Variance — run an old forecast forward to now and compare it
//                 against what actually happened.
// ═══════════════════════════════════════════════════════════════════

import { simulate, DEFAULTS } from './finance.js';
import { C } from './format.js';

// The accounts we track through history. Order is display order.
// `excluded` marks the cash ISA, which by design sits outside every
// wealth and retirement calculation.
export const ACCOUNTS = [
  { key: 'jPen', label: 'James Pension', settingKey: 'jamesPension', colour: C.blue },
  { key: 'oPen', label: 'Joanne Pension', settingKey: 'joannePension', colour: C.pink },
  { key: 'jISA', label: 'James S&S ISA', settingKey: 'jamesISA', colour: '#38BDF8' },
  { key: 'oISA', label: 'Joanne S&S ISA', settingKey: 'joanneISA', colour: '#FDA4AF' },
  { key: 'jPB', label: 'James Premium Bonds', settingKey: 'jamesPB', colour: C.gold },
  { key: 'oPB', label: 'Joanne Premium Bonds', settingKey: 'joannePB', colour: '#E5CA6C' },
  { key: 'cNet', label: 'Company (net)', settingKey: null, colour: C.teal },
  { key: 'jCash', label: 'James Cash ISA', settingKey: 'jamesCashISA', colour: '#8B9DC8', excluded: true },
  { key: 'oCash', label: 'Joanne Cash ISA', settingKey: 'joanneCashISA', colour: '#6F7FA8', excluded: true },
];

/**
 * Reduce a settings object to the same shape a simulate() row has, so
 * recorded actuals and forecast predictions can be compared directly.
 * Mirrors simulate()'s definition of `total` exactly — cash ISAs excluded.
 */
export function accountsFromSettings(s) {
  const cNet = (s.company || 0) * 0.9;
  const jPB = s.jamesPB || 0, oPB = s.joannePB || 0;
  const jISA = s.jamesISA || 0, oISA = s.joanneISA || 0;
  const jPen = s.jamesPension || 0, oPen = s.joannePension || 0;
  const jCash = s.jamesCashISA || 0, oCash = s.joanneCashISA || 0;
  return {
    jPB, oPB, jISA, oISA, jPen, oPen, jCash, oCash, cNet,
    total: jPB + oPB + jISA + oISA + jPen + oPen + cNet,
  };
}

// Absolute month number, for measuring elapsed time on the model's own axis.
export const monthIndex = (year, month) => (year || 0) * 12 + ((month || 1) - 1);

/**
 * Where a snapshot's forecast says the accounts should be, `elapsed`
 * months after that snapshot was taken.
 *
 * simulate()'s row 0 is the END of the start month (growth and
 * contributions already applied), so elapsed=0 is the raw starting
 * balances and elapsed=N is row N-1.
 */
export function predictedAt(sim, settings, elapsed) {
  if (elapsed <= 0) return accountsFromSettings(settings);
  const row = sim[Math.min(elapsed - 1, sim.length - 1)];
  return row || accountsFromSettings(settings);
}

/**
 * Forecast vs actual.
 *
 * Runs `base`'s forecast forward to the point in time represented by
 * `actualSettings`, then compares per account and in total.
 *
 * Direction convention: `delta = actual - predicted`, so a POSITIVE
 * delta means the real balance came in ABOVE what that old forecast
 * predicted — i.e. ahead of plan.
 *
 * @param {{label:string, createdAt:string, settings:object}} base  the past snapshot
 * @param {object} actualSettings  the latest recorded actuals
 * @returns {{ monthsElapsed, truncated, rows[], totalRow }}
 */
export function varianceReport(base, actualSettings) {
  const bs = base.settings;
  const from = monthIndex(bs.startYear, bs.startMonth);
  const to = monthIndex(actualSettings.startYear, actualSettings.startMonth);
  let monthsElapsed = to - from;

  // If the as-of dates were not maintained, fall back to real elapsed
  // calendar time so the comparison still means something.
  if (!Number.isFinite(monthsElapsed) || monthsElapsed < 0) monthsElapsed = 0;

  const sim = simulate(bs);
  const truncated = monthsElapsed > sim.length;
  const predicted = predictedAt(sim, bs, monthsElapsed);
  const actual = accountsFromSettings(actualSettings);

  const mkRow = (key, label, colour, excluded) => {
    const p = predicted[key] || 0;
    const a = actual[key] || 0;
    const delta = a - p;
    return {
      key, label, colour, excluded,
      predicted: p, actual: a, delta,
      pct: p !== 0 ? (delta / Math.abs(p)) * 100 : (a === 0 ? 0 : Infinity),
      ahead: delta >= 0,
    };
  };

  const rows = ACCOUNTS.map(a => mkRow(a.key, a.label, a.colour, !!a.excluded));
  const totalRow = mkRow('total', 'Total investable', C.gold, false);

  return { monthsElapsed, truncated, rows, totalRow, predicted, actual };
}

/**
 * Trend series built purely from recorded snapshots — real history, not
 * projection. Returns points in chronological order.
 */
export function trendSeries(snapshots) {
  const ordered = [...snapshots].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
  return ordered.map(s => ({
    id: s.id,
    createdAt: s.createdAt,
    label: s.label,
    // The balances describe the snapshot's as-of date, which may differ
    // from the day it happened to be saved. Plot against the former.
    asOf: asOfLabel(s.settings),
    accounts: accountsFromSettings(s.settings),
  }));
}

const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Nov 2026" from a settings object's simulation start. */
export function asOfLabel(settings) {
  const m = MONTH_ABBR[settings?.startMonth] || '';
  const y = settings?.startYear || '';
  return (m && y) ? `${m} ${y}` : (settings?.asOfDate || '—');
}

// ── Snapshot construction ───────────────────────────────────────────

export function makeSnapshot(label, settings) {
  return {
    id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2)),
    createdAt: new Date().toISOString(),
    label: (label || '').trim() || defaultLabel(),
    // Deep clone so later edits to the working settings can never reach
    // back into a saved snapshot.
    settings: JSON.parse(JSON.stringify(settings)),
  };
}

export function defaultLabel() {
  return new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) + ' checkup';
}

// ── Backup format ───────────────────────────────────────────────────

export const EXPORT_FORMAT = 'jj-retirement-planner-backup';
export const EXPORT_VERSION = 1;

export function buildExport(settings, snapshots) {
  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    settings,
    snapshots: [...snapshots].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
  };
}

/**
 * Validate and normalise an imported backup file.
 * Throws with a plain-English message rather than letting bad data in.
 */
export function parseImport(text) {
  let data;
  try { data = JSON.parse(text); }
  catch { throw new Error('That file is not valid JSON — it may be damaged or not a backup file.'); }

  if (!data || typeof data !== 'object') throw new Error('That file does not contain a backup.');
  if (data.format !== EXPORT_FORMAT) {
    throw new Error('That file is not a backup from this app.');
  }
  if (typeof data.version !== 'number' || data.version > EXPORT_VERSION) {
    throw new Error(`That backup was made by a newer version of the app (v${data.version}).`);
  }
  if (!data.settings || typeof data.settings !== 'object') {
    throw new Error('That backup is missing its settings.');
  }

  const snapshots = Array.isArray(data.snapshots) ? data.snapshots : [];
  for (const s of snapshots) {
    if (!s || typeof s !== 'object' || !s.id || !s.createdAt || !s.settings) {
      throw new Error('That backup contains a damaged snapshot and was not imported.');
    }
  }

  return {
    // Merge over DEFAULTS so a backup from an older version still opens.
    settings: { ...DEFAULTS, ...data.settings },
    snapshots: snapshots.map(s => ({
      id: String(s.id),
      createdAt: new Date(s.createdAt).toISOString(),
      label: String(s.label || defaultLabel()),
      settings: { ...DEFAULTS, ...s.settings },
    })),
    exportedAt: data.exportedAt || null,
  };
}
